/* iter-53o — Extracted from BookDetailModal.jsx to keep that component under
 * 250 lines. This subcomponent owns all POD (print-on-demand softcover) state:
 * the open toggle, pricing fetch, signed-cover flag, shipping address form,
 * and the checkout call. The parent only passes the book id + a `navigate`
 * callback for the sign-in redirect path.
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import { Truck, PenLine } from 'lucide-react';
import { bookstoreApi } from '@/lib/bookstoreApi';

function Field({ label, testid, value, onChange, className = '' }) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-0.5 block text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{label}</span>
      <input
        type="text"
        value={value || ''}
        onChange={(e) => onChange?.(e.target.value)}
        data-testid={testid}
        className="h-8 w-full rounded-sm border border-border bg-background px-2 text-[11.5px] outline-none focus:border-primary"
      />
    </label>
  );
}

export default function BookDetailPodPane({ book, user, onRequireSignIn }) {
  const [podOpen, setPodOpen] = useState(false);
  const [podPricing, setPodPricing] = useState(null);
  const [podSigned, setPodSigned] = useState(false);
  const [podBusy, setPodBusy] = useState(false);
  const [podAddr, setPodAddr] = useState({
    name: '', line1: '', line2: '', city: '', region: '', postal: '', country: 'US', phone: '',
  });

  const openPod = async () => {
    if (!user) {
      toast.info('Sign in to your Book Store account to order a printed copy.');
      onRequireSignIn?.();
      return;
    }
    setPodOpen(true);
    if (!podPricing) {
      try {
        const p = await bookstoreApi.podPricing(book.id);
        setPodPricing(p);
      } catch (_e) {
        toast.error('Could not load print pricing right now.');
      }
    }
  };

  const startPodCheckout = async () => {
    const required = ['name', 'line1', 'city', 'region', 'postal', 'country', 'phone'];
    for (const k of required) {
      if (!String(podAddr[k] || '').trim()) {
        toast.error(`Please enter ${k}.`);
        return;
      }
    }
    setPodBusy(true);
    try {
      const { url } = await bookstoreApi.checkoutPod({
        book_id: book.id,
        signed: podSigned,
        origin_url: window.location.origin,
        address: podAddr,
      });
      window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start print checkout.');
      setPodBusy(false);
    }
  };

  if (!podOpen) {
    return (
      <div className="mt-4 rounded-md border border-border bg-card/60 p-3">
        <button
          type="button"
          onClick={openPod}
          data-testid={`order-print-${book.id}`}
          className="inline-flex items-center gap-2 text-[12.5px] font-semibold text-primary hover:underline"
        >
          <Truck className="h-3.5 w-3.5" />
          Order a printed softcover — including signed edition
        </button>
      </div>
    );
  }

  return (
    <div className="mt-4 rounded-md border border-border bg-card/60 p-3">
      <div className="space-y-3" data-testid={`pod-pane-${book.id}`}>
        <div className="flex items-center justify-between gap-2">
          <div className="text-[12px] font-semibold">Print-On-Demand softcover</div>
          <button
            type="button"
            onClick={() => setPodOpen(false)}
            className="text-[11px] text-muted-foreground hover:underline"
            data-testid={`pod-close-${book.id}`}
          >
            Cancel
          </button>
        </div>
        {!podPricing ? (
          <div className="text-[11px] text-muted-foreground">Loading pricing…</div>
        ) : (
          <>
            <label className="flex items-start gap-2 text-[12px]">
              <input
                type="checkbox"
                checked={podSigned}
                onChange={(e) => setPodSigned(e.target.checked)}
                data-testid={`pod-signed-${book.id}`}
                className="mt-0.5"
              />
              <span>
                <span className="font-semibold">Signed by the author</span>{' '}
                <span className="text-muted-foreground">
                  (+${podPricing.signed.toFixed(2)} — we email the author and queue printing only after they confirm)
                </span>
              </span>
            </label>
            <div className="grid grid-cols-2 gap-2 text-[11px]">
              <Field label="Name" testid={`pod-name-${book.id}`}
                     value={podAddr.name}
                     onChange={(v) => setPodAddr({ ...podAddr, name: v })} />
              <Field label="Country" testid={`pod-country-${book.id}`}
                     value={podAddr.country}
                     onChange={(v) => setPodAddr({ ...podAddr, country: v })} />
              <Field label="Address line 1" testid={`pod-line1-${book.id}`} className="col-span-2"
                     value={podAddr.line1}
                     onChange={(v) => setPodAddr({ ...podAddr, line1: v })} />
              <Field label="Address line 2 (optional)" testid={`pod-line2-${book.id}`} className="col-span-2"
                     value={podAddr.line2}
                     onChange={(v) => setPodAddr({ ...podAddr, line2: v })} />
              <Field label="City" testid={`pod-city-${book.id}`}
                     value={podAddr.city}
                     onChange={(v) => setPodAddr({ ...podAddr, city: v })} />
              <Field label="State / Province" testid={`pod-region-${book.id}`}
                     value={podAddr.region}
                     onChange={(v) => setPodAddr({ ...podAddr, region: v })} />
              <Field label="Postal code" testid={`pod-postal-${book.id}`}
                     value={podAddr.postal}
                     onChange={(v) => setPodAddr({ ...podAddr, postal: v })} />
              <Field label="Phone" testid={`pod-phone-${book.id}`} className="col-span-2"
                     value={podAddr.phone}
                     onChange={(v) => setPodAddr({ ...podAddr, phone: v })} />
            </div>
            <p className="text-[10px] text-muted-foreground">
              Required by our printer for delivery updates — never shared for marketing.
            </p>
            <div className="flex items-center justify-between border-t border-border pt-2 text-[11.5px]">
              <div className="text-muted-foreground">
                {podSigned
                  ? <>Softcover ${podPricing.base.toFixed(2)} + Signed ${podPricing.signed.toFixed(2)} + Shipping ${podPricing.shipping.toFixed(2)}</>
                  : <>Softcover ${podPricing.base.toFixed(2)} + Shipping ${podPricing.shipping.toFixed(2)}</>}
                {podPricing.length_surcharge > 0 && (
                  <> + Long-book surcharge ${podPricing.length_surcharge.toFixed(2)}</>
                )}
              </div>
              <div className="text-[13px] font-semibold text-primary" data-testid={`pod-total-${book.id}`}>
                ${(podSigned ? podPricing.total_signed : podPricing.total_unsigned).toFixed(2)}
              </div>
            </div>
            {podPricing.length_surcharge > 0 && (
              <p className="text-[10px] text-muted-foreground" data-testid={`pod-surcharge-note-${book.id}`}>
                This book is {podPricing.page_count} pages — a small surcharge over {podPricing.surcharge_threshold_pages} pages helps cover the real print cost of longer novels.
              </p>
            )}
            <button
              type="button"
              onClick={startPodCheckout}
              disabled={podBusy}
              data-testid={`pod-checkout-${book.id}`}
              className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-[12.5px] font-semibold text-primary-foreground transition hover:bg-primary/90 disabled:opacity-60"
            >
              {podSigned ? <PenLine className="h-4 w-4" /> : <Truck className="h-4 w-4" />}
              {podBusy ? 'Starting checkout…' : (podSigned ? 'Order signed copy' : 'Order printed copy')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
